// Authentication provider - manages user login, logout, and session state
import React, { useMemo, createContext, useCallback, useContext, useState, useEffect } from "react";
import { useToast } from "./ToastProvider";
import { useLocalization } from "./LocalizationProvider";
import { API_BASE_URL } from "../config/api";
import { getUserIdFromToken } from "../lib/utils/jwt";
import { logger } from "../lib/utils/logger";

// Language mapping from API response to our language codes
const languageMapping = {
  'french': 'fr',
  'english': 'en',
  'portuguese': 'pt',
  'spanish': 'es',
  'fr': 'fr',
  'en': 'en',
  'pt': 'pt',
  'es': 'es'
};

// Role mapping from API response to our expected format
const roleMapping = {
  'admin': 'admin',
  'user': 'user',
  'representative': 'representative'
};

/**
 * Fetch the current user from /user/whoami and map it to our internal format
 * @param {string} accessToken - JWT token
 * @param {string} fallbackName - Name used when the API does not return an email
 * @returns {Promise<Object|null>} Complete user or null if the request failed
 */
async function fetchCompleteUser(accessToken, fallbackName) {
  const whoamiResponse = await fetch(`${API_BASE_URL}/user/whoami`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  });

  if (!whoamiResponse.ok) {
    return null;
  }

  const userData = await whoamiResponse.json();
  logger.debug('Complete user data from API', { userId: userData.id });

  // Transform API response to match our expected format
  const apiCommonLanguage = userData.commonLanguage?.name?.toLowerCase();
  const apiCulturalLanguage = userData.culturalLanguage?.toLowerCase();
  const apiRole = userData.role?.role?.toLowerCase();

  logger.debug('API language data', {
    apiCommonLanguage,
    apiCulturalLanguage,
    apiRole,
    mappedCommonLanguage: languageMapping[apiCommonLanguage],
    mappedCulturalLanguage: languageMapping[apiCulturalLanguage],
    mappedRole: roleMapping[apiRole]
  });

  return {
    id: userData.id,
    name: userData.email || fallbackName, // Use email as name if available
    firstName: userData.firstName,
    lastName: userData.lastName,
    email: userData.email,
    role: roleMapping[apiRole],
    commonLanguage: languageMapping[apiCommonLanguage] || 'fr', // Map to our language codes
    // Cultural language is free text: keep the raw value so profile saves don't overwrite it
    culturalLanguage: languageMapping[apiCulturalLanguage] || userData.culturalLanguage || ''
  };
}

const RestAuthCtx = createContext({ user: /** @type {User|null} */(null), token: /** @type {string|null} */(null), userId: /** @type {string|null} */(null), login: async (_n,_p)=>{}, logout: ()=>{}, refreshUser: async ()=>{} });

export function RestAuthProvider({ children }){
  // Authentication state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);
  
  // Get hooks for notifications and localization
  const { notify } = useToast();
  const { resetManualLanguageFlag } = useLocalization();

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const savedToken = localStorage.getItem("auth.token");
      const savedUser = localStorage.getItem("auth.user");
      if (savedToken) {
        setToken(savedToken);
        // Decode userId from token
        const decodedUserId = getUserIdFromToken(savedToken);
        setUserId(decodedUserId);
      }
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        logger.debug('Restored user from localStorage', { userId: parsedUser.id });
        setUser(parsedUser);
      }
    } catch {}
  }, []);

  const login = useCallback(async (name, password) => {
    try {
      logger.debug('API login endpoint', { url: `${API_BASE_URL}/auth/login` });
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: name, password })
      });

      if (!response.ok) {
        notify({ type: 'error', message: 'Identifiants invalides.' });
        return false;
      }

      const data = await response.json();
      const accessToken = data?.accessToken;
      const roleFromApi = data?.user?.role || data?.role; // try to read role if provided
      if (!accessToken) {
        notify({ type: 'error', message: "Réponse d'authentification invalide." });
        return false;
      }

      // DEBUG: force known admin user id for memory datasource when using admin@cs.com
      const isDebugAdmin = typeof name === 'string' && name.toLowerCase() === 'admin@cs.com';
      const debugUserId = "42962c3a-aefb-4d4d-be39-b28e7431cf8a";

      setToken(accessToken);
      
      // Decode userId from token
      const decodedUserId = getUserIdFromToken(accessToken);
      setUserId(decodedUserId);

      // Fetch complete user data from /user/whoami endpoint
      try {
        const completeUser = await fetchCompleteUser(accessToken, name);

        if (completeUser) {
          completeUser.role = completeUser.role || roleFromApi?.toLowerCase(); // Ensure lowercase role fallback
          logger.debug('Final complete user object', { userId: completeUser.id });

          setUser(completeUser);
          
          // Persist complete user data
          try {
            localStorage.setItem("auth.token", accessToken);
            localStorage.setItem("auth.user", JSON.stringify(completeUser));
          } catch {}
        } else {
          // Fallback to basic user data if whoami fails
          const loggedUser = {
            id: isDebugAdmin ? debugUserId : (data?.user?.id || data?.id),
            name,
            role: isDebugAdmin ? 'admin' : (roleMapping[roleFromApi?.toLowerCase()] || roleFromApi?.toLowerCase()),
            commonLanguage: 'fr' // Default fallback
          };
          logger.debug('Using fallback user data', { userId: loggedUser.id });
          setUser(loggedUser);
          
          try {
            localStorage.setItem("auth.token", accessToken);
            localStorage.setItem("auth.user", JSON.stringify(loggedUser));
          } catch {}
        }
      } catch (error) {
        logger.error('Failed to fetch user data', error);
        // Fallback to basic user data
        const loggedUser = {
          id: isDebugAdmin ? debugUserId : (data?.user?.id || data?.id),
          name,
          role: isDebugAdmin ? 'admin' : (roleMapping[roleFromApi?.toLowerCase()] || roleFromApi?.toLowerCase()),
          commonLanguage: 'fr' // Default fallback
        };
        logger.debug('Using error fallback user data', { userId: loggedUser.id });
        setUser(loggedUser);
        
        try {
          localStorage.setItem("auth.token", accessToken);
          localStorage.setItem("auth.user", JSON.stringify(loggedUser));
        } catch {}
      }

      notify({ type: 'success', message: `Bienvenue ${name} !` });
      return true;
    } catch (err) {
      notify({ type: 'error', message: 'Erreur réseau lors de la connexion.' });
      return false;
    }
  }, [notify]);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setUserId(null);
    try {
      localStorage.removeItem("auth.token");
      localStorage.removeItem("auth.user");
    } catch {}
    resetManualLanguageFlag(); // Reset manual language flag on logout
    notify({ type: 'info', message: 'Déconnecté.' });
  }, [notify, resetManualLanguageFlag]);

  // Re-fetch the current user after their profile was updated
  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const completeUser = await fetchCompleteUser(token, user?.name);
      if (!completeUser) return;
      setUser(completeUser);
      try {
        localStorage.setItem("auth.user", JSON.stringify(completeUser));
      } catch {}
    } catch (error) {
      logger.error('Failed to refresh user data', error);
    }
  }, [token, user?.name]);

  return <RestAuthCtx.Provider value={useMemo(()=>({ user, token, userId, login, logout, refreshUser }), [user, token, userId, login, logout, refreshUser])}>{children}</RestAuthCtx.Provider>;
}

export const useRestAuth = () => useContext(RestAuthCtx); 